// Validation utilities for form fields
// Matches server-side validation constraints from cpfgs-api

/**
 * Validation result with optional error message
 */
export interface ValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validate email address
 * Server constraints: @Email, @Size(max=255)
 */
export const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

/**
 * Validate email with detailed error message
 * Server constraints: @Email, @Size(max=255)
 */
export const validateEmailWithMessage = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return { valid: false, error: 'กรุณากรอกอีเมล' }
  }
  if (email.length > 255) {
    return { valid: false, error: 'อีเมลต้องไม่เกิน 255 ตัวอักษร' }
  }
  if (!validateEmail(email)) {
    return { valid: false, error: 'กรุณากรอกอีเมลที่ถูกต้อง' }
  }
  return { valid: true }
}

/**
 * Validate password (simple check for login)
 * Login is less strict - server validates credentials
 */
export const validatePassword = (password: string): boolean => {
  return password.length >= 1
}

/**
 * Validate password for user creation/update
 * Server constraints: @Size(min=8, max=128), must contain uppercase, lowercase, and digit
 */
export const validatePasswordStrong = (password: string, isRequired: boolean = true): ValidationResult => {
  if (!password) {
    if (isRequired) {
      return { valid: false, error: 'กรุณากรอกรหัสผ่าน' }
    }
    return { valid: true } // Optional password (edit mode) - empty is OK
  }

  if (password.length < 8) {
    return { valid: false, error: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร' }
  }

  if (password.length > 128) {
    return { valid: false, error: 'รหัสผ่านต้องไม่เกิน 128 ตัวอักษร' }
  }

  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: 'รหัสผ่านต้องมีตัวอักษรพิมพ์ใหญ่อย่างน้อย 1 ตัว' }
  }

  if (!/[a-z]/.test(password)) {
    return { valid: false, error: 'รหัสผ่านต้องมีตัวอักษรพิมพ์เล็กอย่างน้อย 1 ตัว' }
  }

  if (!/[0-9]/.test(password)) {
    return { valid: false, error: 'รหัสผ่านต้องมีตัวเลขอย่างน้อย 1 ตัว' }
  }

  if (/\s/.test(password)) {
    return { valid: false, error: 'รหัสผ่านต้องไม่มีช่องว่าง' }
  }

  return { valid: true }
}

/**
 * Validate username
 * Server constraints: @NotBlank, @Size(min=3, max=100)
 */
export const validateUsername = (username: string): ValidationResult => {
  if (!username || !username.trim()) {
    return { valid: false, error: 'กรุณากรอกชื่อผู้ใช้' }
  }

  const trimmed = username.trim()

  if (trimmed.length < 3) {
    return { valid: false, error: 'ชื่อผู้ใช้ต้องมีอย่างน้อย 3 ตัวอักษร' }
  }

  if (trimmed.length > 100) {
    return { valid: false, error: 'ชื่อผู้ใช้ต้องไม่เกิน 100 ตัวอักษร' }
  }

  return { valid: true }
}

/**
 * Validate text field with size constraints
 */
export const validateText = (
  text: string,
  fieldName: string,
  minLength: number = 0,
  maxLength: number = 255,
  required: boolean = false
): ValidationResult => {
  if (!text || !text.trim()) {
    if (required) {
      return { valid: false, error: `กรุณากรอก${fieldName}` }
    }
    return { valid: true }
  }

  const trimmed = text.trim()

  if (minLength > 0 && trimmed.length < minLength) {
    return { valid: false, error: `${fieldName}ต้องมีอย่างน้อย ${minLength} ตัวอักษร` }
  }

  if (trimmed.length > maxLength) {
    return { valid: false, error: `${fieldName}ต้องไม่เกิน ${maxLength} ตัวอักษร` }
  }

  return { valid: true }
}

/**
 * Validate pagination page number
 * Server constraints: min=0
 */
export const validatePage = (page: number): boolean => {
  return Number.isInteger(page) && page >= 0
}

/**
 * Validate pagination page size
 * Server constraints: min=1, max=100
 */
export const validatePageSize = (size: number): boolean => {
  return Number.isInteger(size) && size >= 1 && size <= 100
}
